import React, { useState, useCallback } from 'react';
import { makeStyles } from 'design-system/product/theme';

import { TaskType } from '@scale/shared-types';
import ExampleViewer from './ExampleViewer';
import { useInstructionTaskData } from 'react-scale/dashboard.scale.com/components/SelfServe/Instructions/hooks';
import { CircularProgress } from 'react-scale/components/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// eslint-disable-next-line restrict-imports/restrict-imports
import { IconButton } from '@material-ui/core';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';

interface ExampleLabelPreviewProps {
  taskId: string;
  isGoodExample: boolean;
  caption?: string;
  index: number;
  readOnly: boolean;
  hasOpenedExample?: (taskId: string, isGoodExample: boolean) => void;
  projectTemplateId?: string;
  preventOpen?: boolean;
  handleDelete?: () => void;
  updateCaption?: (c: string) => void;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  tasksWithProcessingAnnotationsResponses?: Record<string, any>;
}

const getAttachmentInfo = (taskData: any) => {
  if (!taskData) {
    return { attachmentType: null, attachment: null };
  }
  let attachmentType, attachment;
  const taskType = taskData.type;

  if (taskType === TaskType.TextCollection) {
    attachmentType = taskData.params.attachments[0].type;
    attachment = taskData.params.attachments[0].content;
  } else if (taskType === TaskType.NamedEntityRecognition) {
    attachmentType = 'text';
    attachment = taskData.params.text;
  } else if (taskType === TaskType.DocumentTranscription) {
    attachmentType = 'image';
    attachment = taskData.params.attachments[0];
  } else {
    attachmentType = taskData.params.attachment_type;
    attachment = taskData.params.attachment;
  }

  return { attachmentType, attachment };
};

/*
 * render a preview of an example task
 * when user clicks on it, open modal with full example shown
 */
export const ExampleLabelPreview: React.FC<ExampleLabelPreviewProps> = ({
  taskId,
  isGoodExample,
  caption,
  readOnly,
  hasOpenedExample,
  projectTemplateId,
  preventOpen,
  handleDelete,
  updateCaption,
  dragHandleProps,
  tasksWithProcessingAnnotationsResponses,
}) => {
  const classes = useStyles();
  const [exampleViewerOpen, setExampleViewerOpen] = useState(false);
  const { isLoading, data: taskData } = useInstructionTaskData(
    taskId,
    readOnly,
    projectTemplateId,
    true,
  );

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      handleDelete && handleDelete();
    },
    [handleDelete],
  );

  if (isLoading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  const { attachmentType, attachment } = getAttachmentInfo(taskData);

  const renderDataPreview = (attachment: string, attachmentType: string) => {
    if (!attachment || !attachmentType) {
      return <div className={classes.text}>Error loading example.</div>;
    }

    // assume unprocessed is image for now
    if (attachmentType === 'image' || attachmentType === 'unprocessed') {
      return <img className={classes.img} src={attachment} crossOrigin="anonymous" />;
    } else if (attachmentType === 'text') {
      return <div className={classes.text}>{attachment}</div>;
    } else if (attachmentType === 'pdf') {
      return <object className={classes.pdf} data={attachment}></object>;
    } else if (attachmentType === 'audio' || attachmentType === 'transcription_audio') {
      return <audio className={classes.audio} src={attachment} controls />;
    } else if (attachmentType === 'website') {
      return <div className={classes.website}>{attachment}</div>;
    } else if (attachmentType === 'video') {
      return <video className={classes.video} src={attachment}></video>;
    } else {
      return null;
    }
  };

  return (
    <div className={classes.label}>
      {exampleViewerOpen && (
        <ExampleViewer
          modalOpen={exampleViewerOpen}
          setModalOpen={arg => setExampleViewerOpen(arg)}
          taskId={taskId}
          taskInfo={{
            ...taskData,
            goodExample: isGoodExample,
            caption,
          }}
          readOnly={readOnly}
          hasOpenedExample={hasOpenedExample}
          projectTemplateId={projectTemplateId}
          updateCaption={updateCaption}
          tasksWithProcessingAnnotationsResponses={tasksWithProcessingAnnotationsResponses}
        />
      )}
      {
        <div
          onClick={async e => {
            e.preventDefault();
            if (!preventOpen) setExampleViewerOpen(true);
          }}
          style={{ cursor: preventOpen ? 'default' : 'pointer', width: '100%', height: '100%' }}
        >
          <div className={classes.preview}>{renderDataPreview(attachment, attachmentType)}</div>
          <div className={classes.overlay} />
          {!readOnly && (
            <div className={classes.checkbox} onClick={handleCheckboxClick}>
              <IconButton size="small">
                <FontAwesomeIcon icon="trash" color="white" />
              </IconButton>
            </div>
          )}
          {!!dragHandleProps && !readOnly && (
            <div className={classes.dragIcon} {...dragHandleProps}>
              <IconButton size="small">
                <FontAwesomeIcon icon="grip-vertical" color="white" />
              </IconButton>
            </div>
          )}
        </div>
      }
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  overlay: {
    background: 'rgba(0, 0, 0, 0.25)',
    position: 'absolute',
    ...theme.alignment?.inset,
    opacity: 0,
    pointerEvents: 'none',
    transition: `opacity ${theme.duration?.short}ms ease-out`,
    zIndex: 1,
  },
  checkbox: {
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(4.5),
    opacity: 0,
    transform: `translate3d(0, ${theme?.spacing(-0.5)}px, 0)`,
    pointerEvents: 'none',
    transition: `all ${theme.duration?.short}ms ease-out`,
    zIndex: 2,
  },
  dragIcon: {
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(1.5),
    opacity: 0,
    transform: `translate3d(0, ${theme?.spacing(-0.5)}px, 0)`,
    pointerEvents: 'none',
    transition: `all ${theme.duration?.short}ms ease-out`,
    zIndex: 2,
  },
  checkboxRoot: {
    padding: 0,
    '@global svg': {
      height: 28,
      width: 28,
    },
  },
  label: {
    '&:hover $button': {
      opacity: 1,
    },
    position: 'relative',
    height: '100%',
    width: '100%',
    '&:hover $checkbox': {
      opacity: 1,
      transform: `translate3d(0, 0, 0)`,
      pointerEvents: 'all',
    },
    '&:hover $dragIcon': {
      opacity: 1,
      transform: `translate3d(0, 0, 0)`,
      pointerEvents: 'all',
    },
    '&:hover $overlay': {
      opacity: 1,
    },
  },
  button: {
    opacity: '0',
    top: '-6px',
    right: '-6px',
    position: 'absolute',
    zIndex: 1,
    color: 'gray',
  },
  preview: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  img: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
  },
  pdf: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  audio: {
    width: '100%',
    height: '100%',
    background: '#dddddd',
  },
  video: {
    width: '100%',
    height: '100%',
    background: '#dddddd',
    padding: '2px',
    boxSizing: 'border-box',
  },
  website: {
    background: '#dddddd',
    fontSize: '10px',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    padding: '4px',
  },
  text: {
    background: '#dddddd',
    fontSize: '10px',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    padding: '4px',
    maxHeight: '120px',
  },
  loading: {
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));
